/**
 * Floor-plan vectors on paper: the DXF export's entities (world meters,
 * y up) → display items in viewport-local paper millimeters (y down), at the
 * viewport's exact scale.
 *
 * One affine map does it — center on the framed point, turn by the heading,
 * scale by 1000 / scaleDen mm per meter, flip y — applied to the coordinates
 * here rather than as a group transform, so pen widths stay true paper
 * millimeters whatever the scale.
 *
 * Text ignores the world height the DXF carries: plan text is set at the
 * PAPER size the sheet chooses per category (the "exact font size" of the
 * export), and kept readable (never upside down).
 */

import type { DxfBlock, DxfColor, DxfEntity, DxfPoint } from '@pointorama/dxf-exporter';
import { PLAN_LAYER_CATEGORIES, PLAN_LAYER_LEGEND_NAMES, PRINT_BLACK, planLayerPen, printColorToCss, planTextSize, textSizeToEmMm, type FontRef, type LegendRow, type PlanLayerCategory, type PlanTextCategory, type PrintColor, type StrokeStyle, type ViewportSettings } from 'shared-types';
import type { DisplayItem, PathCommand } from '../displayList';
import type { FontResolver } from '../fonts';
import { apply, applyVector, bulgeCubics, compose, degrees, rotate, scale, translate, type Matrix, type Pt } from '../geometry';
import { measureLine } from '../text';

export interface PlanRenderInput {
  entities: readonly DxfEntity[];
  blocks: readonly DxfBlock[];
  settings: ViewportSettings;
  widthMm: number;
  heightMm: number;
  fonts: FontResolver;
  /** Face for plan text (technical lettering, e.g. osifont). */
  font: FontRef;
  /** Layer name → pen category; null = not drawn. Defaults to {@link defaultLayerCategory}. */
  layerCategory?: (layerName: string) => PlanLayerCategory | null;
  /** Layer name → text size category. Defaults to {@link defaultTextCategory}. */
  textCategory?: (layerName: string) => PlanTextCategory;
}

const LAYER_SUFFIXES: readonly PlanLayerCategory[] = ['Walls', 'Openings', 'Outlines', 'Objects', 'Dimensions', 'Rooms', 'RoomTags', 'Polylines', 'Polygons', 'Points', 'Beams', 'Columns'];

/** `<Floor>_<Suffix>` / `<Suffix>` → category; every `Labels_<Kind>` is `Labels`. */
export function defaultLayerCategory(layerName: string): PlanLayerCategory | null {
  if (/(^|_)Labels_[A-Za-z]+$/.test(layerName)) return 'Labels';
  for (const suffix of LAYER_SUFFIXES) {
    if (layerName === suffix || layerName.endsWith(`_${suffix}`)) return suffix;
  }
  return null;
}

export function defaultTextCategory(layerName: string): PlanTextCategory {
  switch (defaultLayerCategory(layerName)) {
    case 'RoomTags':
    case 'Rooms':
      return 'roomTags';
    case 'Openings':
      return 'openings';
    case 'Dimensions':
    case 'Objects':
      return 'dimensions';
    default:
      return 'labels';
  }
}

/** World (meters, y up) → viewport-local paper millimeters (y down). */
export function worldToViewportMatrix(settings: Pick<ViewportSettings, 'center' | 'headingDeg' | 'scaleDen'>, widthMm: number, heightMm: number): Matrix {
  const mmPerMeter = 1000 / settings.scaleDen;
  return compose(translate(widthMm / 2, heightMm / 2), scale(mmPerMeter, -mmPerMeter), rotate(-settings.headingDeg), translate(-settings.center.x, -settings.center.y));
}

/** ISO 128 dashed line: 3 mm dash, 1.5 mm gap (scaled up for heavy pens). */
function dashFor(weightMm: number): number[] {
  const f = Math.max(1, weightMm / 0.35);
  return [3 * f, 1.5 * f];
}

/** DXF colors are screen colors: white / near-white (ACI 7) means "ink" on paper. */
function dxfColorToPrint(color: DxfColor | undefined): PrintColor | null {
  if (!color) return null;
  if (color.r > 240 && color.g > 240 && color.b > 240) return PRINT_BLACK;
  return { space: 'rgb', r: color.r / 255, g: color.g / 255, b: color.b / 255 };
}

interface Context {
  input: PlanRenderInput;
  layerCategory: (layerName: string) => PlanLayerCategory | null;
  textCategory: (layerName: string) => PlanTextCategory;
  blocks: Map<string, DxfBlock>;
  out: DisplayItem[];
  /** Cull box in viewport mm (the viewport, padded for strokes and text). */
  cull: { minX: number; minY: number; maxX: number; maxY: number };
}

interface Inherited {
  matrix: Matrix;
  layerName: string;
  color: DxfColor | undefined;
  depth: number;
}

const MAX_BLOCK_DEPTH = 8;

function penFor(ctx: Context, layerName: string, entityColor: DxfColor | undefined): { stroke: StrokeStyle; color: PrintColor; category: PlanLayerCategory } | null {
  const category = ctx.layerCategory(layerName);
  if (category === null) return null;
  const pen = planLayerPen(ctx.input.settings, category);
  if (!pen.visible) return null;
  const color = pen.color ?? (ctx.input.settings.monochrome ? PRINT_BLACK : (dxfColorToPrint(entityColor) ?? PRINT_BLACK));
  return { stroke: { color, weightMm: pen.weightMm, dashMm: [], cap: 'round', join: 'round' }, color, category };
}

function visible(ctx: Context, points: readonly Pt[]): boolean {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return maxX >= ctx.cull.minX && minX <= ctx.cull.maxX && maxY >= ctx.cull.minY && minY <= ctx.cull.maxY;
}

/** Path commands for a (possibly bulged) DXF polyline; also returns every mapped point for culling. */
function polylineCommands(points: readonly DxfPoint[], bulges: readonly number[] | undefined, closed: boolean, m: Matrix): { commands: PathCommand[]; extent: Pt[] } {
  const commands: PathCommand[] = [];
  const extent: Pt[] = [];
  if (points.length === 0) return { commands, extent };
  const first = apply(m, points[0].x, points[0].y);
  commands.push({ op: 'M', x: first.x, y: first.y });
  extent.push(first);
  const segmentCount = closed ? points.length : points.length - 1;
  for (let i = 0; i < segmentCount; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const curves = bulgeCubics(a, b, bulges?.[i] ?? 0);
    if (curves.length === 0) {
      const p = apply(m, b.x, b.y);
      commands.push({ op: 'L', x: p.x, y: p.y });
      extent.push(p);
      continue;
    }
    for (const curve of curves) {
      const c1 = apply(m, curve.c1.x, curve.c1.y);
      const c2 = apply(m, curve.c2.x, curve.c2.y);
      const to = apply(m, curve.to.x, curve.to.y);
      commands.push({ op: 'C', x1: c1.x, y1: c1.y, x2: c2.x, y2: c2.y, x: to.x, y: to.y });
      extent.push(c1, c2, to);
    }
  }
  if (closed) commands.push({ op: 'Z' });
  return { commands, extent };
}

function ringCommands(ring: readonly DxfPoint[], m: Matrix, extent: Pt[]): PathCommand[] {
  if (ring.length < 3) return [];
  const result = polylineCommands(ring, undefined, true, m);
  extent.push(...result.extent);
  return result.commands;
}

function drawText(ctx: Context, entity: Extract<DxfEntity, { type: 'TEXT' }>, inherited: Inherited, color: PrintColor, layer: PlanLayerCategory): void {
  if (!entity.text) return;
  const category = ctx.textCategory(inherited.layerName);
  const size = planTextSize(ctx.input.settings, category);
  const face = ctx.input.fonts.resolve(ctx.input.font, 'normal', false);
  const em = textSizeToEmMm(size, face.capHeightRatio);
  const cap = face.capHeightRatio * em;
  const anchor = apply(inherited.matrix, entity.position.x, entity.position.y);
  // Direction of the text baseline on paper (the world rotation through the full map).
  const r = ((entity.rotation ?? 0) * Math.PI) / 180;
  const dir = applyVector(inherited.matrix, Math.cos(r), Math.sin(r));
  // Room tags name an area, not a line: always level on the page, whatever the plan rotation.
  let angle = category === 'roomTags' ? 0 : degrees(Math.atan2(dir.y, dir.x));
  let hAlign = entity.hAlign ?? 0;
  let vAlign = entity.vAlign ?? 0;
  // Never upside down (vertical text reads bottom to top): turn half a revolution and mirror the anchor.
  if (angle >= 90 - 1e-6 || angle < -90 - 1e-6) {
    angle = angle > 0 ? angle - 180 : angle + 180;
    hAlign = hAlign === 1 ? 1 : 2 - hAlign;
    vAlign = vAlign === 2 ? 2 : vAlign === 3 ? 0 : 3;
  }
  const lines = entity.text.split('\n');
  const pitch = 1.25 * em;
  const rad = (angle * Math.PI) / 180;
  const ux = Math.cos(rad);
  const uy = Math.sin(rad);
  // Paper "down" relative to the baseline direction.
  const vx = -uy;
  const vy = ux;
  const blockH = cap + pitch * (lines.length - 1);
  // Offset of the FIRST baseline below the anchor, along v.
  const firstBaseline = vAlign === 3 ? cap : vAlign === 2 ? cap - blockH / 2 : -(pitch * (lines.length - 1));
  lines.forEach((line, i) => {
    const width = measureLine(line, face, em, 0);
    const along = hAlign === 1 ? -width / 2 : hAlign === 2 ? -width : 0;
    const across = firstBaseline + i * pitch;
    const x = anchor.x + ux * along + vx * across;
    const y = anchor.y + uy * along + vy * across;
    const corners = [
      { x, y },
      { x: x + ux * width, y: y + uy * width },
      { x: x - vx * cap, y: y - vy * cap },
    ];
    if (!visible(ctx, corners)) return;
    ctx.out.push({ type: 'text', x, y, text: line, font: face.key, sizeMm: em, color, letterSpacingMm: 0, rotationDeg: angle, layer });
  });
}

function drawEntity(ctx: Context, entity: DxfEntity, parent: Inherited): void {
  // Block content on layer 0 / BYBLOCK color takes the INSERT's layer / color.
  const layerName = !entity.layerName || entity.layerName === '0' ? parent.layerName : entity.layerName;
  const entityColor = entity.colorAci === 0 || !entity.color ? parent.color : entity.color;
  const inherited: Inherited = { ...parent, layerName, color: entityColor };

  if (entity.type === 'INSERT') {
    const block = ctx.blocks.get(entity.blockName);
    if (!block || parent.depth >= MAX_BLOCK_DEPTH) return;
    const base = block.basePoint ?? { x: 0, y: 0, z: 0 };
    const local = compose(translate(entity.position.x, entity.position.y), rotate(entity.rotation ?? 0), scale(entity.xScale ?? 1, entity.yScale ?? 1), translate(-base.x, -base.y));
    const child: Inherited = { matrix: compose(parent.matrix, local), layerName, color: entityColor, depth: parent.depth + 1 };
    for (const inner of block.entities) drawEntity(ctx, inner, child);
    return;
  }

  const pen = penFor(ctx, layerName, entityColor);
  if (!pen) return;
  const m = parent.matrix;
  switch (entity.type) {
    case 'LINE': {
      const a = apply(m, entity.start.x, entity.start.y);
      const b = apply(m, entity.end.x, entity.end.y);
      if (!visible(ctx, [a, b])) return;
      ctx.out.push({
        type: 'path',
        commands: [
          { op: 'M', x: a.x, y: a.y },
          { op: 'L', x: b.x, y: b.y },
        ],
        stroke: pen.stroke,
        fill: null,
        fillRule: 'nonzero',
        layer: pen.category,
      });
      return;
    }
    case 'POLYLINE': {
      const { commands, extent } = polylineCommands(entity.points, entity.bulges, entity.closed, m);
      if (commands.length < 2 || !visible(ctx, extent)) return;
      const stroke = entity.dashed ? { ...pen.stroke, dashMm: dashFor(pen.stroke.weightMm), cap: 'butt' as const } : pen.stroke;
      ctx.out.push({ type: 'path', commands, stroke, fill: null, fillRule: 'nonzero', layer: pen.category });
      return;
    }
    case 'HATCH': {
      const extent: Pt[] = [];
      const commands = [entity.outerBoundary, ...entity.islands].flatMap((ring) => ringCommands(ring, m, extent));
      if (commands.length === 0 || !visible(ctx, extent)) return;
      ctx.out.push({ type: 'path', commands, stroke: null, fill: pen.color, fillRule: 'evenodd', layer: pen.category });
      return;
    }
    case 'POINT': {
      const p = apply(m, entity.position.x, entity.position.y);
      if (!visible(ctx, [p])) return;
      // A dot the size of the pen: a zero-length round-capped stroke.
      ctx.out.push({
        type: 'path',
        commands: [
          { op: 'M', x: p.x, y: p.y },
          { op: 'L', x: p.x, y: p.y },
        ],
        stroke: { ...pen.stroke, weightMm: Math.max(pen.stroke.weightMm * 3, 0.5), cap: 'round' },
        fill: null,
        fillRule: 'nonzero',
        layer: pen.category,
      });
      return;
    }
    case 'TEXT':
      drawText(ctx, entity, inherited, pen.color, pen.category);
      return;
  }
}

/** Display items in viewport-local mm (0..widthMm, 0..heightMm); the caller clips to the viewport. */
export function planToDisplayItems(input: PlanRenderInput): DisplayItem[] {
  const margin = 20;
  const ctx: Context = {
    input,
    layerCategory: input.layerCategory ?? defaultLayerCategory,
    textCategory: input.textCategory ?? defaultTextCategory,
    blocks: new Map(input.blocks.map((block) => [block.name, block])),
    out: [],
    cull: { minX: -margin, minY: -margin, maxX: input.widthMm + margin, maxY: input.heightMm + margin },
  };
  const root: Inherited = { matrix: worldToViewportMatrix(input.settings, input.widthMm, input.heightMm), layerName: '0', color: undefined, depth: 0 };
  for (const entity of input.entities) drawEntity(ctx, entity, root);
  return ctx.out;
}

export interface PlanBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** World XY bounds of plan entities (block inserts count by their insertion point); null when empty. */
export function planEntityBounds(entities: readonly DxfEntity[]): PlanBounds | null {
  let bounds: PlanBounds | null = null;
  const add = (p: DxfPoint) => {
    if (!Number.isFinite(p.x) || !Number.isFinite(p.y)) return;
    if (!bounds) bounds = { minX: p.x, minY: p.y, maxX: p.x, maxY: p.y };
    else bounds = { minX: Math.min(bounds.minX, p.x), minY: Math.min(bounds.minY, p.y), maxX: Math.max(bounds.maxX, p.x), maxY: Math.max(bounds.maxY, p.y) };
  };
  for (const entity of entities) {
    switch (entity.type) {
      case 'LINE':
        add(entity.start);
        add(entity.end);
        break;
      case 'POLYLINE':
        entity.points.forEach(add);
        break;
      case 'HATCH':
        entity.outerBoundary.forEach(add);
        break;
      case 'POINT':
      case 'TEXT':
      case 'INSERT':
        add(entity.position);
        break;
    }
  }
  return bounds;
}

/**
 * The colors a drawing uses, as legend rows: one per color (its CSS value
 * the key), named after the layer when only one layer draws in it — empty
 * when several share it — with a line swatch at the heaviest line drawn in
 * it (a filled swatch for colors only areas use). Plan items only (tagged
 * with their layer); rows follow the layer order.
 */
export function drawingLegendRows(items: readonly DisplayItem[]): LegendRow[] {
  const found = new Map<string, { color: PrintColor; layers: Set<PlanLayerCategory>; weightMm: number; line: boolean }>();
  const note = (color: PrintColor, layer: PlanLayerCategory, weightMm: number, line: boolean) => {
    const key = printColorToCss(color);
    const entry = found.get(key) ?? { color, layers: new Set<PlanLayerCategory>(), weightMm: 0, line: false };
    entry.layers.add(layer);
    entry.weightMm = Math.max(entry.weightMm, weightMm);
    entry.line ||= line;
    found.set(key, entry);
  };
  const walk = (list: readonly DisplayItem[]) => {
    for (const item of list) {
      if (item.type === 'group') walk(item.items);
      else if (item.type === 'path' && item.layer) {
        if (item.stroke) note(item.stroke.color, item.layer, item.stroke.weightMm, true);
        else if (item.fill) note(item.fill, item.layer, 0, false);
      } else if (item.type === 'text' && item.layer) note(item.color, item.layer, 0, true);
    }
  };
  walk(items);
  const order = (layers: Set<PlanLayerCategory>) => Math.min(...[...layers].map((layer) => PLAN_LAYER_CATEGORIES.indexOf(layer)));
  return [...found.entries()]
    .sort(([, a], [, b]) => order(a.layers) - order(b.layers))
    .map(([key, entry]) => ({
      key,
      defaultLabel: entry.layers.size === 1 ? PLAN_LAYER_LEGEND_NAMES[[...entry.layers][0]] : '',
      color: entry.color,
      swatch: entry.line ? 'line' : 'fill',
      weightMm: entry.weightMm,
    }));
}

